import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useConfig } from '../contexts/ConfigContext'
import SenhaMasterModal from '../components/SenhaMasterModal'
import IconEditar from '../components/icons/IconEditar'
import { corClara } from '../utils/cor'
import { avisar, confirmar } from '../components/Avisos'

// Recibo de Socios (09/10) — substitui a planilha de descontos dos socios. Regra de qual recibo cada
// parcela cai fica no backend (routes/socios.js); aqui so repete reciboDe() pra mostrar a dica no
// formulario. So admin (tem salario dos socios).
// Compras do cartao sao lancadas UMA vez, no Fluxo de Caixa, com "Descontar de" o socio (opcao B
// escolhida pelo usuario) e aparecem aqui com a etiqueta "Fluxo". O "+ Debito/+ Credito" daqui fica
// pro que nao passa pelo Fluxo (adiantamento, compra ja em andamento, ajuste).

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const fmtData = (d) => d ? d.split('-').reverse().join('/') : '—'
const fmtValor = (v) => `R$ ${(parseFloat(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const tituloRecibo = (nome, d) => {
  const [a, m, dia] = d.split('-')
  return `Recibo ${nome.split(' ')[0]} ${a} - ${dia} de ${MESES[parseInt(m, 10) - 1]}`
}

// Mesmos helpers de moeda do Fluxo de Caixa: os 2 ultimos digitos sao sempre os centavos.
const parseMoneyInput = (v) => {
  const s = (v || '').toString()
  const n = parseFloat(s.replace(/[^0-9,]/g, '').replace(',', '.')) || 0
  return s.includes('-') ? -n : n
}
const formatMoneyInput = (v) => {
  const n = parseFloat(v) || 0
  return (n < 0 ? '-' : '') + 'R$ ' + Math.abs(n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}
const maskMoneyInput = (v) => {
  const digitos = (v || '').toString().replace(/\D/g, '').replace(/^0+/, '')
  return digitos ? formatMoneyInput(parseInt(digitos, 10) / 100) : ''
}

const iso = (a, m, d) => `${a}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
const ultimoDiaMes = (a, m) => new Date(Date.UTC(a, m, 0)).getUTCDate()
const reciboDe = (socio, dataISO) => {
  if (!socio || !/^\d{4}-\d{2}-\d{2}$/.test(dataISO || '')) return null
  const dias = (a, m) => [socio.dia_recibo_1, socio.dia_recibo_2].map(d => iso(a, m, Math.min(d, ultimoDiaMes(a, m)))).sort()
  const [a, m] = dataISO.split('-').map(Number)
  const r = dias(a, m).find(x => x >= dataISO)
  if (r) return r
  return m === 12 ? dias(a + 1, 1)[0] : dias(a, m + 1)[0]
}

const hojeISO = () => { const h = new Date(); return iso(h.getFullYear(), h.getMonth() + 1, h.getDate()) }

// ===== Modal de debito/credito =====
function LancamentoModal({ socio, tipoInicial, lancamento, onClose, onSaved }) {
  const [form, setForm] = useState(() => lancamento ? {
    tipo: lancamento.tipo, descricao: lancamento.descricao, valor: formatMoneyInput(lancamento.valor),
    data_referencia: lancamento.data_referencia, parcelado: !!lancamento.total_parcelas,
    total_parcelas: lancamento.total_parcelas ? String(lancamento.total_parcelas) : '', parcela_inicial: String(lancamento.parcela_inicial || 1),
  } : { tipo: tipoInicial, descricao: '', valor: '', data_referencia: hojeISO(), parcelado: false, total_parcelas: '', parcela_inicial: '1' })
  const [salvando, setSalvando] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const recibo = reciboDe(socio, form.data_referencia)

  const salvar = async () => {
    setSalvando(true)
    try {
      const corpo = {
        tipo: form.tipo, descricao: form.descricao, valor: form.valor, data_referencia: form.data_referencia,
        total_parcelas: form.parcelado ? form.total_parcelas : null, parcela_inicial: form.parcelado ? form.parcela_inicial : 1,
      }
      if (lancamento) await api.put(`/socios/lancamentos/${lancamento.id}`, corpo)
      else await api.post(`/socios/${socio.id}/lancamentos`, corpo)
      onSaved()
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally { setSalvando(false) }
  }
  const excluir = async () => {
    if (!(await confirmar(`Excluir "${lancamento.descricao}"${lancamento.total_parcelas ? ' e todas as parcelas dele' : ''}? Recibos já fechados não mudam.`))) return
    await api.delete(`/socios/lancamentos/${lancamento.id}`)
    onSaved()
  }

  const nomeTipo = form.tipo === 'credito' ? 'Crédito' : 'Débito'
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">{lancamento ? `Editar ${nomeTipo.toLowerCase()}` : `Novo ${nomeTipo.toLowerCase()}`} — {socio.nome.split(' ')[0]}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Tipo</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['debito', 'Débito (desconta)'], ['credito', 'Crédito (soma)']].map(([v, l]) => (
              <button key={v} type="button" className={`btn btn-sm ${form.tipo === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => set('tipo', v)}>{l}</button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Descrição *</label>
          <input className="form-input" value={form.descricao} onChange={e => set('descricao', e.target.value)} autoFocus placeholder="Ex.: Ótica Otto" />
        </div>
        <div className="form-group">
          <label className="form-label">{form.parcelado ? 'Valor da parcela *' : 'Valor *'}</label>
          <input className="form-input" value={form.valor} onChange={e => set('valor', maskMoneyInput(e.target.value))} placeholder="R$ 0,00" />
        </div>
        <div className="form-group">
          <label className="form-label">{form.parcelado ? 'Vencimento da fatura da 1ª parcela abaixo *' : 'Vencimento da fatura ou data do gasto *'}</label>
          <input className="form-input" type="date" value={form.data_referencia} onChange={e => set('data_referencia', e.target.value)} />
          <div style={{ fontSize: 12, color: '#7A7F87', marginTop: 4 }}>
            {recibo ? <>Cai no recibo de <b>{fmtData(recibo)}</b>{form.parcelado ? ' (as seguintes, um mês depois cada)' : ''}</> : 'Informe a data'}
          </div>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.parcelado} onChange={e => set('parcelado', e.target.checked)} /> Parcelado
        </label>
        {form.parcelado && (
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Começa na parcela</label>
              <input className="form-input" type="number" min="1" value={form.parcela_inicial} onChange={e => set('parcela_inicial', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">de (total) *</label>
              <input className="form-input" type="number" min="2" value={form.total_parcelas} onChange={e => set('total_parcelas', e.target.value)} />
            </div>
          </div>
        )}
        {form.parcelado && (
          <div style={{ fontSize: 12, color: '#7A7F87', marginTop: -6, marginBottom: 12 }}>
            Compra que já está em andamento: informe a parcela atual em "Começa na parcela" e o vencimento da fatura dela.
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 8 }}>
          <div>{lancamento && <button type="button" className="btn btn-danger" onClick={excluir}>Excluir</button>}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ===== Configurar socios =====
function SociosModal({ socios, onClose, onSaved }) {
  const [destinos, setDestinos] = useState([])
  const vazio = { id: null, nome: '', origem_destino_id: '', dia_recibo_1: '', dia_recibo_2: '', salario: '' }
  const [form, setForm] = useState(vazio)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  useEffect(() => {
    api.get('/cadastro/origem-destino?tipo=saida').then(r => setDestinos(Array.isArray(r.data) ? r.data : [])).catch(() => {})
  }, [])
  const editar = (s) => setForm({ id: s.id, nome: s.nome, origem_destino_id: s.origem_destino_id || '', dia_recibo_1: String(s.dia_recibo_1), dia_recibo_2: String(s.dia_recibo_2), salario: formatMoneyInput(s.salario) })
  const salvar = async () => {
    try {
      if (form.id) await api.put(`/socios/${form.id}`, form)
      else await api.post('/socios', form)
      setForm(vazio)
      onSaved()
    } catch (err) { avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message)) }
  }
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Sócios</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {socios.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#F7F8FA', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
            <span><b>{s.nome}</b> — dias {s.dia_recibo_1} e {s.dia_recibo_2} · salário {fmtValor(s.salario)} · destino: {s.origem_destino_nome || <span style={{ color: '#A32D2D' }}>não definido</span>}</span>
            <button type="button" className="btn-icon" title="Editar" onClick={() => editar(s)}><IconEditar /></button>
          </div>
        ))}
        <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>{form.id ? 'Editar sócio' : 'Novo sócio'}</div>
          <div className="form-group">
            <label className="form-label">Nome *</label>
            <input className="form-input" value={form.nome} onChange={e => set('nome', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">1º dia de recibo *</label>
              <input className="form-input" type="number" min="1" max="31" value={form.dia_recibo_1} onChange={e => set('dia_recibo_1', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">2º dia de recibo *</label>
              <input className="form-input" type="number" min="1" max="31" value={form.dia_recibo_2} onChange={e => set('dia_recibo_2', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1.4 }}>
              <label className="form-label">Salário por recibo</label>
              <input className="form-input" value={form.salario} onChange={e => set('salario', maskMoneyInput(e.target.value))} placeholder="R$ 0,00" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Destino no Fluxo de Caixa (para a saída ao fechar o recibo)</label>
            <select className="form-input" value={form.origem_destino_id} onChange={e => set('origem_destino_id', e.target.value)}>
              <option value="">— selecione —</option>
              {destinos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {form.id && <button type="button" className="btn btn-secondary" onClick={() => setForm(vazio)}>Cancelar edição</button>}
            <button type="button" className="btn btn-primary" onClick={salvar}>{form.id ? 'Salvar' : '+ Adicionar'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ===== Fechar recibo =====
function FecharModal({ recibo, onClose, onFechado }) {
  const [dataPagamento, setDataPagamento] = useState(recibo.data)
  const [categorias, setCategorias] = useState([])
  const [categoriaId, setCategoriaId] = useState('')
  const [salvando, setSalvando] = useState(false)
  const gera = recibo.total_receber > 0

  useEffect(() => {
    if (!gera) return
    api.get('/cadastro/categorias-financeiras?tipo=saida').then(r => {
      const cats = Array.isArray(r.data) ? r.data : []
      const porId = Object.fromEntries(cats.map(c => [c.id, c]))
      setCategorias(cats.filter(c => c.nivel === 3).map(c3 => {
        const c2 = porId[c3.categoria_pai_id], c1 = c2 ? porId[c2.categoria_pai_id] : null
        return { id: c3.id, caminho: [c1?.nome, c2?.nome, c3.nome].filter(Boolean).join(' › ') }
      }))
    }).catch(() => {})
    if (recibo.socio.origem_destino_id) {
      api.get(`/financeiro/ultima-categoria?origem_destino_id=${recibo.socio.origem_destino_id}`)
        .then(r => r.data?.categoria_financeira_id && setCategoriaId(String(r.data.categoria_financeira_id))).catch(() => {})
    }
  }, [])

  const fechar = async () => {
    setSalvando(true)
    try {
      await api.post(`/socios/${recibo.socio.id}/recibo/fechar`, { data: recibo.data, data_pagamento: dataPagamento, categoria_financeira_id: categoriaId || null })
      onFechado()
    } catch (err) {
      avisar('Erro ao fechar: ' + (err.response?.data?.erro || err.message))
    } finally { setSalvando(false) }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <span className="modal-title">Fechar recibo de {fmtData(recibo.data)}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {gera ? (
          <>
            <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
              Será lançada no Fluxo de Caixa uma <b>saída de {fmtValor(recibo.total_receber)}</b> para <b>{recibo.socio.origem_destino_nome || '(destino não definido)'}</b>, com vencimento em {fmtData(recibo.data)}.
            </p>
            <div className="form-group">
              <label className="form-label">Data de pagamento (deixe vazio se ainda não pagou)</label>
              <input className="form-input" type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Categoria</label>
              <select className="form-input" value={categoriaId} onChange={e => setCategoriaId(e.target.value)}>
                <option value="">— sem categoria —</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.caminho}</option>)}
              </select>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
            O recibo fecha negativo ({fmtValor(recibo.total_receber)}). Nada é lançado no Fluxo de Caixa e o valor passa como <b>Saldo anterior</b> para o próximo recibo.
          </p>
        )}
        <p style={{ fontSize: 12, color: '#7A7F87' }}>Depois de fechado, o recibo não muda mais, mesmo se algum lançamento for editado.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={fechar} disabled={salvando}>{salvando ? 'Fechando...' : 'Fechar recibo'}</button>
        </div>
      </div>
    </div>
  )
}

// Recibo pra imprimir/salvar em PDF — mesmo visual do Relatorio de Indicacoes (Indicacoes.jsx):
// faixa na cor da corretora, cards de resumo, tabelas zebradas e linha de assinatura.
// A janela de impressao nao enxerga as CSS vars do app nem resolve caminho relativo, por isso cor e
// logo chegam ja resolvidos (hex / URL absoluta).
const imprimirRecibo = (recibo, config) => {
  const esc = (t) => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
  const cor = config?.cor_primaria || '#DD3B00'
  const corBg = corClara(cor)
  const nomeCorretora = config?.nome || ''
  const logo = new URL(config?.logo || '/logo_emseg.png', window.location.origin).href
  const hoje = new Date().toLocaleDateString('pt-BR')
  const [a, m, d] = recibo.data.split('-')
  const dataExtenso = `${parseInt(d, 10)} de ${MESES[parseInt(m, 10) - 1]} de ${a}`
  const negativo = recibo.total_receber < 0

  const linhas = (itens) => itens.map((i, k) => `<tr style="background:${k % 2 ? '#FAFAFA' : '#fff'};${i.saldo_anterior ? 'font-style:italic;' : ''}">
      <td>${esc(i.origem_destino) || '<span class="mut">—</span>'}</td>
      <td class="b">${esc(i.descricao)}</td>
      <td class="c">${i.parcela ? `${i.parcela}${i.de ? ` / ${i.de}` : ''}` : '<span class="mut">—</span>'}</td>
      <td class="v">${fmtValor(i.valor)}</td>
    </tr>`).join('')
  const tabela = (titulo, itens, total, corTotal) => `
    <div class="sec"><span>${titulo}</span><span class="qt">${itens.length} ${itens.length === 1 ? 'lançamento' : 'lançamentos'}</span></div>
    <table>
      <thead><tr><th style="width:28%">Origem/Destino</th><th>Descrição</th><th class="c" style="width:80px">Parcela</th><th class="v" style="width:120px">Valor</th></tr></thead>
      <tbody>${linhas(itens) || '<tr><td colspan="4" class="c mut" style="padding:12px">Nenhum lançamento</td></tr>'}</tbody>
      <tfoot><tr><td colspan="3" class="v">Total ${titulo.toLowerCase()}</td><td class="v" style="color:${corTotal}">${fmtValor(total)}</td></tr></tfoot>
    </table>`

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(tituloRecibo(recibo.socio.nome, recibo.data))}</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;background:#fff;color:#333;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .page{max-width:800px;margin:0 auto;padding:20px;}
  .topo{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;}
  .topo img{max-height:52px;max-width:200px;object-fit:contain;}
  .topo .corr{font-size:13px;font-weight:700;color:#555;text-align:right;}
  .hdr{background:${cor};color:#fff;padding:14px 20px;border-radius:6px;display:flex;justify-content:space-between;align-items:center;}
  .hdr h1{margin:0;font-size:16px;font-weight:700;letter-spacing:.04em;}
  .hdr .dt{font-size:12px;opacity:.9;text-align:right;}
  .info{display:flex;justify-content:space-between;font-size:12px;color:#555;margin:10px 2px 14px;}
  .info b{color:#222;}
  .st{display:inline-block;padding:2px 10px;border-radius:10px;font-size:10px;font-weight:700;text-transform:uppercase;}
  .cards{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:18px;}
  .card{border:1px solid #eee;border-radius:6px;padding:10px 14px;}
  .card .l{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.05em;}
  .card .n{font-size:17px;font-weight:700;margin-top:4px;}
  .card.dest{background:${corBg};border-color:${corBg};}
  .sec{display:flex;justify-content:space-between;align-items:baseline;font-size:12px;font-weight:700;color:${cor};text-transform:uppercase;letter-spacing:.05em;margin:16px 0 6px;padding-bottom:4px;border-bottom:2px solid ${corBg};}
  .sec .qt{font-size:10px;color:#999;font-weight:400;text-transform:none;letter-spacing:0;}
  table{width:100%;border-collapse:collapse;font-size:11px;}
  th{text-align:left;padding:7px 8px;background:${corBg};color:${cor};font-size:10px;text-transform:uppercase;letter-spacing:.04em;}
  td{padding:6px 8px;border-bottom:1px solid #eee;}
  td.b{font-weight:600;} .c{text-align:center;} .v{text-align:right;} .mut{color:#bbb;}
  tfoot td{border-bottom:none;border-top:2px solid #ddd;font-weight:700;font-size:12px;padding-top:8px;}
  .ass{display:flex;justify-content:space-around;margin-top:60px;}
  .ass div{width:38%;border-top:1px solid #555;text-align:center;font-size:11px;color:#555;padding-top:5px;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:30px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4 portrait;margin:12mm;}.page{padding:0;}}
</style></head><body>
<div class="page">
  <div class="topo"><img src="${esc(logo)}" alt="" onerror="this.style.display='none'"><div class="corr">${esc(nomeCorretora)}</div></div>
  <div class="hdr"><h1>RECIBO DE SÓCIO</h1><div class="dt">${dataExtenso}</div></div>
  <div class="info">
    <div>Sócio: <b>${esc(recibo.socio.nome)}</b></div>
    <div>Situação: <span class="st" style="background:${recibo.fechado ? '#E8F5E9;color:#2E7D32' : '#FFF4E5;color:#B26A00'}">${recibo.fechado ? 'Fechado' : 'Aberto'}</span></div>
  </div>
  <div class="cards">
    <div class="card"><div class="l">Créditos</div><div class="n" style="color:#1a3a6e">${fmtValor(recibo.total_creditos)}</div></div>
    <div class="card"><div class="l">Débitos</div><div class="n" style="color:#A32D2D">${fmtValor(recibo.total_debitos)}</div></div>
    <div class="card dest"><div class="l">Total a Receber</div><div class="n" style="color:${negativo ? '#A32D2D' : cor}">${fmtValor(recibo.total_receber)}</div></div>
  </div>
  ${tabela('Débitos', recibo.debitos, recibo.total_debitos, '#A32D2D')}
  ${tabela('Créditos', recibo.creditos, recibo.total_creditos, '#1a3a6e')}
  ${negativo ? `<p style="font-size:11px;color:#A32D2D;margin-top:12px;">Recibo negativo: a diferença de ${fmtValor(-recibo.total_receber)} passa como Saldo anterior para o próximo recibo.</p>` : ''}
  <div class="ass"><div>${esc(recibo.socio.nome)}</div><div>${esc(nomeCorretora) || 'Corretora'}</div></div>
  <div class="footer">Documento gerado pelo sistema Corretora &mdash; ${hoje}</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

  const w = window.open('', '_blank')
  if (!w) { avisar('Libere as janelas pop-up deste site para imprimir.'); return }
  w.document.write(html)
  w.document.close()
}

export default function RecibosSocios() {
  const { usuario, ehGestor } = useAuth()
  // Gerente usa o recibo, mas so o admin configura os socios (backend tambem bloqueia).
  const ehAdmin = usuario?.perfil === 'admin'
  const { config } = useConfig()
  const navigate = useNavigate()
  const [socios, setSocios] = useState([])
  const [socioId, setSocioId] = useState(null)
  const [dataRecibo, setDataRecibo] = useState('')
  const [recibo, setRecibo] = useState(null)
  const [lancamentos, setLancamentos] = useState([])
  const [modalLanc, setModalLanc] = useState(null)
  const [modalSocios, setModalSocios] = useState(false)
  const [modalFechar, setModalFechar] = useState(false)
  const [pedirSenhaReabrir, setPedirSenhaReabrir] = useState(false)
  const [verTodos, setVerTodos] = useState(false)
  const socioIdRef = useRef(null)

  useEffect(() => { if (usuario && !ehGestor) navigate('/home', { replace: true }) }, [usuario])

  const carregarSocios = () => api.get('/socios').then(r => {
    const lista = Array.isArray(r.data) ? r.data : []
    setSocios(lista)
    if (!socioIdRef.current && lista.length) { socioIdRef.current = lista[0].id; setSocioId(lista[0].id) }
    if (!lista.length && ehAdmin) setModalSocios(true)
  }).catch(() => {})
  useEffect(() => { carregarSocios() }, [])

  const carregarRecibo = () => {
    if (!socioId) return
    api.get(`/socios/${socioId}/recibo${dataRecibo ? `?data=${dataRecibo}` : ''}`).then(r => setRecibo(r.data)).catch(() => setRecibo(null))
    api.get(`/socios/${socioId}/lancamentos`).then(r => setLancamentos(Array.isArray(r.data) ? r.data : [])).catch(() => setLancamentos([]))
  }
  useEffect(() => { carregarRecibo() }, [socioId, dataRecibo])

  const socio = socios.find(s => s.id === socioId)
  if (usuario && !ehGestor) return null

  const trocarSocio = (id) => { socioIdRef.current = id; setSocioId(id); setDataRecibo(''); setRecibo(null) }
  const abrirItem = (i) => {
    if (recibo?.fechado || !i.lancamento_id) return
    const l = lancamentos.find(x => x.id === i.lancamento_id)
    if (l) setModalLanc({ lancamento: l })
  }
  const reabrir = async (senha) => {
    await api.post(`/socios/${socioId}/recibo/reabrir`, { data: recibo.data, senha_master: senha })
    setPedirSenhaReabrir(false)
    carregarRecibo()
  }

  const tabela = (titulo, itens, total, cor) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3a6e', marginBottom: 6 }}>{titulo}</div>
      <div className="table-wrap">
        <table className="table-compacta">
          <thead><tr><th>Origem/Destino</th><th>Descrição</th><th style={{ width: 70, textAlign: 'center' }}>Parcela</th><th style={{ width: 70, textAlign: 'center' }}>de</th><th style={{ width: 130, textAlign: 'right' }}>Valor</th></tr></thead>
          <tbody>
            {itens.map((i, k) => {
              const clicavel = !recibo.fechado && i.lancamento_id
              return (
                <tr key={k} onClick={() => abrirItem(i)} style={{ cursor: clicavel ? 'pointer' : 'default', fontStyle: i.saldo_anterior ? 'italic' : undefined }} title={clicavel ? 'Clique para editar' : undefined}>
                  <td>{i.origem_destino || ''}</td>
                  <td>
                    {i.descricao}
                    {i.financeiro_id && <span title="Lançado no Fluxo de Caixa com 'Descontar de' — edite por lá" style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#EEF2F8', color: '#1a3a6e' }}>Fluxo</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>{i.parcela ?? ''}</td>
                  <td style={{ textAlign: 'center' }}>{i.de ?? ''}</td>
                  <td style={{ textAlign: 'right' }}>{fmtValor(i.valor)}</td>
                </tr>
              )
            })}
            {itens.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#aaa' }}>Nenhum</td></tr>}
          </tbody>
          <tfoot><tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td><td style={{ textAlign: 'right', fontWeight: 700, color: cor }}>{fmtValor(total)}</td></tr></tfoot>
        </table>
      </div>
    </div>
  )

  return (
    <>
      <div className="topbar">
        <h2>Recibo de Sócios</h2>
        {ehAdmin && <button className="btn btn-secondary" onClick={() => setModalSocios(true)}>Configurar sócios</button>}
      </div>
      <div className="page-content">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {socios.map(s => (
            <button key={s.id} type="button" className={`btn ${s.id === socioId ? 'btn-primary' : 'btn-secondary'}`} onClick={() => trocarSocio(s.id)}>{s.nome}</button>
          ))}
        </div>

        {socio && recibo && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDataRecibo(recibo.anterior)} title="Recibo anterior">◀</button>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{tituloRecibo(socio.nome, recibo.data)}</div>
                  <div style={{ fontSize: 12, color: recibo.fechado ? '#2E7D32' : '#7A7F87' }}>
                    {recibo.fechado ? `Fechado${recibo.financeiro_id ? ' · saída lançada no Fluxo de Caixa' : ''}` : 'Aberto'}
                  </div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDataRecibo(recibo.seguinte)} title="Próximo recibo">▶</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDataRecibo('')}>Hoje</button>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#7A7F87' }}>Total a Receber</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: recibo.total_receber < 0 ? '#A32D2D' : 'var(--primary)' }}>{fmtValor(recibo.total_receber)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {!recibo.fechado && <button type="button" className="btn btn-primary btn-sm" onClick={() => setModalLanc({ tipo: 'debito' })}>+ Débito</button>}
              {!recibo.fechado && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModalLanc({ tipo: 'credito' })}>+ Crédito</button>}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => imprimirRecibo(recibo, config)}>Imprimir / PDF</button>
              {recibo.pode_fechar && <button type="button" className="btn btn-primary btn-sm" onClick={() => setModalFechar(true)}>Fechar recibo</button>}
              {recibo.pode_reabrir && <button type="button" className="btn btn-danger btn-sm" onClick={() => setPedirSenhaReabrir(true)}>Reabrir recibo</button>}
            </div>

            {tabela('DÉBITOS', recibo.debitos, recibo.total_debitos, '#A32D2D')}
            {tabela('CRÉDITOS', recibo.creditos, recibo.total_creditos, '#1a3a6e')}

            <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setVerTodos(v => !v)}>
                {verTodos ? 'Ocultar' : 'Ver'} todos os lançamentos de {socio.nome.split(' ')[0]} ({lancamentos.length})
              </button>
              {verTodos && (
                <div className="table-wrap" style={{ marginTop: 10 }}>
                  <table className="table-compacta">
                    <thead><tr><th>Descrição</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Valor</th><th style={{ textAlign: 'center' }}>Parcelas</th><th>1º recibo</th><th>Último recibo</th><th></th></tr></thead>
                    <tbody>
                      {lancamentos.map(l => (
                        <tr key={l.id}>
                          <td>{l.descricao}</td>
                          <td>{l.tipo === 'credito' ? 'Crédito' : 'Débito'}</td>
                          <td style={{ textAlign: 'right' }}>{fmtValor(l.valor)}</td>
                          <td style={{ textAlign: 'center' }}>{l.total_parcelas ? `${l.parcela_inicial} a ${l.total_parcelas}` : '—'}</td>
                          <td>{fmtData(l.primeiro_recibo)}</td>
                          <td>{fmtData(l.ultimo_recibo)}</td>
                          <td><button type="button" className="btn-icon" title="Editar" onClick={() => setModalLanc({ lancamento: l })}><IconEditar /></button></td>
                        </tr>
                      ))}
                      {lancamentos.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#aaa' }}>Nenhum lançamento</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {modalLanc && socio && (
        <LancamentoModal socio={socio} tipoInicial={modalLanc.tipo} lancamento={modalLanc.lancamento}
          onClose={() => setModalLanc(null)} onSaved={() => { setModalLanc(null); carregarRecibo() }} />
      )}
      {modalSocios && ehAdmin && (
        <SociosModal socios={socios} onClose={() => setModalSocios(false)} onSaved={() => { carregarSocios(); carregarRecibo() }} />
      )}
      {modalFechar && recibo && (
        <FecharModal recibo={recibo} onClose={() => setModalFechar(false)} onFechado={() => { setModalFechar(false); carregarRecibo() }} />
      )}
      {pedirSenhaReabrir && (
        <SenhaMasterModal
          titulo="Reabrir recibo?"
          mensagem={`O recibo de ${fmtData(recibo.data)} volta a ficar aberto${recibo.financeiro_id ? ' e a saída lançada no Fluxo de Caixa é excluída' : ''}.`}
          onCancelar={() => setPedirSenhaReabrir(false)}
          onConfirmar={reabrir}
        />
      )}
    </>
  )
}
